import React, { useState } from "react";
import dayjs from "dayjs";
import NetaqInput from "./NetaqInput";
import { useTranslate } from "./TranslationText";
import NewActionButton from "./ActionButtons/NewActionButton";
import { filterButtonsContainer, filterCard, filterHeader, filterResetBtn, filterSubmitBtn } from "../common/cssclasses";
import { toSafeISOString } from "../helper/timeUtils";

// Flutter API filter keys:
// newsTitle, newsCategory (From EAHS / About EAHS), newsClassification (General / Activities and events), newsDateFrom, newsDateTo

const MIN_NEWS_DATE = dayjs('2000-01-01');

const initFilterState = {
  newsName: "",           // → API key: newsTitle
  newscategory: "",       // → API key: newsCategory  (empty = All)
  newsClassification: "", // → API key: newsClassification (empty = All)
  DateFrom: "",
  DateTo: "",
};

const NewsFilter = ({ onSubmit, currentFilters }) => {
  const { translate } = useTranslate();
  const [dateStartFrom, setDateStartFrom] = useState(() => {
    return currentFilters?.DateFrom ? dayjs(currentFilters.DateFrom) : null;
  });
  const [dateStartTo, setDateStartTo] = useState(() => {
    return currentFilters?.DateTo ? dayjs(currentFilters.DateTo) : null;
  });
  const [filterState, setFilterState] = useState(() => {
    return currentFilters && Object.keys(currentFilters).length > 0
      ? { ...initFilterState, ...currentFilters }
      : initFilterState;
  });

  React.useEffect(() => {
    if (currentFilters) {
      setFilterState({
        newsName: currentFilters.newsName ?? "",
        newscategory: currentFilters.newscategory ?? "",
        newsClassification: currentFilters.newsClassification ?? "",
        DateFrom: currentFilters.DateFrom ?? "",
        DateTo: currentFilters.DateTo ?? "",
      });
      setDateStartFrom(currentFilters.DateFrom ? dayjs(currentFilters.DateFrom) : null);
      setDateStartTo(currentFilters.DateTo ? dayjs(currentFilters.DateTo) : null);
    }
  }, [currentFilters]);

  const handleChange = (event, input) => {
    if (input === "DateFrom") {
      let val = event;
      if (val && dayjs(val).isValid()) {
        if (dayjs(val).isAfter(dayjs(), 'day')) {
          val = dayjs();
        }
        if (dateStartTo && dayjs(val).isAfter(dayjs(dateStartTo), 'day')) {
          setDateStartTo(null);
          setFilterState(prev => ({
            ...prev,
            DateFrom: toSafeISOString(val) ?? "",
            DateTo: "",
          }));
          setDateStartFrom(val);
          return;
        }
      }
      setDateStartFrom(val);
      setFilterState(prev => ({
        ...prev,
        DateFrom: toSafeISOString(val) ?? "",
      }));
      return;
    }
    if (input === "DateTo") {
      let val = event;
      if (val && dayjs(val).isValid()) {
        if (dayjs(val).isAfter(dayjs(), 'day')) {
          val = dayjs();
        }
        if (dateStartFrom && dayjs(val).isBefore(dayjs(dateStartFrom), 'day')) {
          val = dateStartFrom;
        }
      }
      setDateStartTo(val);
      setFilterState(prev => ({
        ...prev,
        DateTo: toSafeISOString(val) ?? "",
      }));
      return;
    }
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFilterState(prev => ({ ...prev, [event.target.name]: value }));
  };

  const handleSubmit = (event) => {
    window.scrollTo(0, 0);
    event.preventDefault();
    onSubmit(filterState);
  };

  const handleReset = () => {
    window.scrollTo(0, 0);
    setDateStartFrom(null);
    setDateStartTo(null);
    const cleared = { ...initFilterState };
    setFilterState(cleared);
    onSubmit(cleared);
  };

  return (
    <form className={filterCard} onSubmit={handleSubmit} onReset={handleReset}>
      <h2 className={filterHeader}>{translate('web.advancedSearch', 'Advanced Search')}</h2>

      {/* Title */}
      <div className="mb-form-group">
        <NetaqInput
          name="newsName"
          label={translate("web.newsTitle", "News Title")}
          placeholder={translate("web.newsTitle", "News Title")}
          type="text"
          onChange={handleChange}
          value={filterState.newsName}
        />
      </div>

      {/* News Type (Category) — Flutter values: "From EAHS", "About EAHS" */}
      <div className="mb-form-group">
        <label>{translate("web.newsType", "News Type")}</label>
        <div className="AuctionFilterRadioContainer">
          <NetaqInput
            name="newscategory"
            type="radio"
            value="From EAHS"
            checked={filterState.newscategory === "From EAHS"}
            label={translate("web.fromEAHS", "From EAHS")}
            onChange={handleChange}
          />
          <NetaqInput
            name="newscategory"
            type="radio"
            value="About EAHS"
            checked={filterState.newscategory === "About EAHS"}
            label={translate("web.aboutEAHS", "About EAHS")}
            onChange={handleChange}
          />
          <NetaqInput
            name="newscategory"
            type="radio"
            value=""
            checked={filterState.newscategory === ""}
            label={translate("web.all", "All")}
            onChange={handleChange}
          />
        </div>
      </div>

      {/* News Classification — Flutter values: "General", "Activities and events" */}
      <div className="mb-form-group">
        <label>{translate("web.newsClassification", "News Classification")}</label>
        <div className="AuctionFilterRadioContainer">
          <NetaqInput
            name="newsClassification"
            type="radio"
            value="General"
            checked={filterState.newsClassification === "General"}
            label={translate("web.general", "General")}
            onChange={handleChange}
          />
          <NetaqInput
            name="newsClassification"
            type="radio"
            value="Activities and events"
            checked={filterState.newsClassification === "Activities and events"}
            label={translate("web.eventsAndActivities", "Events And Activities")}
            onChange={handleChange}
          />
          <NetaqInput
            name="newsClassification"
            type="radio"
            value=""
            checked={filterState.newsClassification === ""}
            label={translate("web.all", "All")}
            onChange={handleChange}
          />
        </div>
      </div>

      {/* Date Range */}
      <div className="mb-form-group-last">
        <div className="row">
          <div className="col-6">
            <NetaqInput
              name="DateFrom"
              type="date"
              label={<>{translate("web.date", "Date")} {translate("web.from", "From")}</>}
              onChange={handleChange}
              value={dateStartFrom}
              minDate={MIN_NEWS_DATE}
              maxDate={dateStartTo && dayjs(dateStartTo).isBefore(dayjs(), 'day') ? dateStartTo : dayjs()}
            />
          </div>
          <div className="col-6">
            <NetaqInput
              name="DateTo"
              type="date"
              label={<>{translate("web.date", "Date")} {translate("web.to", "To")}</>}
              onChange={handleChange}
              value={dateStartTo}
              minDate={dateStartFrom || MIN_NEWS_DATE}
              maxDate={dayjs()}
            />
          </div>
        </div>
      </div>

      <div className={filterButtonsContainer}>
        <div className={filterResetBtn}>
          <NewActionButton
            isButton
            classNames="btn-outline-secondary"
            type="reset"
            text={translate('web.reset', 'Reset')}
          />
        </div>
        <div className={filterSubmitBtn}>
          <NewActionButton
            isButton
            classNames="btn-danger"
            text={translate("web.search", "Search")}
            type="submit"
          />
        </div>
      </div>
    </form>
  );
};

export default NewsFilter;
